import React, { useEffect, useRef, useState, useCallback, FC } from 'react';
import { Chart } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { FinancialHealthStats } from '../../types/financialStats';
import { getHealthStatusCode, HEALTH_STATUS_TEXT } from '../../utils/healthStatus';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';
import ChartExpandModal from './ChartExpandModal';
import { useMobileChart } from '../../context/MobileChartContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
);

interface SpendingFactorChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  monthsToShow?: number;
  onExpandClick?: React.MutableRefObject<any>;
}

// Line style options for different chart visualizations
const LINE_STYLES = {
  STRAIGHT: 0,     // Direct lines between points
  CURVED: 0.4,     // Smooth curved lines (good for trends)
};

// Main color for spending charts
export const CHART_COLOR = '#F97316'; // orange-500

const SpendingFactorChart: FC<SpendingFactorChartProps> = ({ 
  data, 
  showDebug = false,
  monthsToShow = 24, // Default to 24 months like VeraScoreChart
  onExpandClick
}) => {
  const chartRef = useRef<any>(null);
  const [visibleData, setVisibleData] = useState<FinancialHealthStats[]>(
    data.slice(-monthsToShow)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Add view window state for chart panning
  const [viewWindow, setViewWindow] = useState({
    start: Math.max(0, data.length - monthsToShow),
    end: data.length
  });
  // Add touch state tracking for drag behavior
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get mobile context
  const { isMobile } = useMobileChart();

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const ctx = chart.ctx;

    // Create improved gradient for the line with glassmorphic style
    const lineGradient = ctx.createLinearGradient(0, 0, 0, 300);
    lineGradient.addColorStop(0, 'rgba(249, 115, 22, 0.95)'); // Brighter orange at top
    lineGradient.addColorStop(0.6, 'rgba(249, 115, 22, 0.9)'); // Orange in middle
    lineGradient.addColorStop(1, 'rgba(234, 88, 12, 0.8)');    // Deeper orange at bottom

    // Create improved gradient for the area with glassmorphic style
    const areaGradient = ctx.createLinearGradient(0, 0, 0, 300);
    areaGradient.addColorStop(0, 'rgba(249, 115, 22, 0.3)');  // Brighter orange at top (higher opacity)
    areaGradient.addColorStop(0.5, 'rgba(249, 115, 22, 0.15)'); // Orange in middle (medium opacity)
    areaGradient.addColorStop(1, 'rgba(255, 255, 255, 0.05)');  // Almost transparent at bottom

    if (chart.data.datasets) {
      chart.data.datasets[0].backgroundColor = areaGradient;
      chart.data.datasets[0].borderColor = lineGradient;
    }

    chart.update();
  }, [visibleData]);

  // Expose the setIsModalOpen function through the onExpandClick prop
  useEffect(() => {
    if (onExpandClick) {
      const handleExpandClick = () => {
        console.log('Opening modal from SpendingFactorChart');
        setIsModalOpen(true);
      };
      
      // This is a bit of a hack, but it allows us to expose the function
      onExpandClick.current = onExpandClick.current || {};
      onExpandClick.current.openModal = handleExpandClick;
      
      // For debugging
      console.log('Set openModal on ref.current in SpendingFactorChart:', onExpandClick.current);
      
      // Also store the chart component for mobile overlay
      if (isMobile && onExpandClick.current) {
        onExpandClick.current.getChartComponent = () => (
          <Chart
            type="line"
            data={chartData}
            options={options}
          />
        );
      }
    }
  }, [isMobile, onExpandClick]);

  // Initialize view window to show only 12 months by default
  useEffect(() => {
    if (data.length > 0) {
      // Initialize with 12 months viewport instead of 24
      const visibleMonths = 12;
      const newStart = Math.max(0, data.length - visibleMonths);
      const newEnd = data.length;
      
      // Update view window
      setViewWindow({ start: newStart, end: newEnd });
      // Update visible data
      setVisibleData(data.slice(newStart, newEnd));
      
      console.log(`Initializing SpendingFactorChart with ${visibleMonths} months viewport`);
    }
  }, [data]);

  // Find key data points (peaks, valleys, significant changes)
  const findKeyDataPoints = () => {
    if (visibleData.length < 3) return [];

    let keyPoints: number[] = [];
    
    // Always include first and last points
    keyPoints.push(0);
    keyPoints.push(visibleData.length - 1);
    
    // Find peaks and valleys
    for (let i = 1; i < visibleData.length - 1; i++) {
      const prev = visibleData[i-1].factor_spending ?? 0;
      const current = visibleData[i].factor_spending ?? 0;
      const next = visibleData[i+1].factor_spending ?? 0;
      
      // Peak
      if (current > prev && current > next) {
        keyPoints.push(i);
      }
      // Valley
      else if (current < prev && current < next) {
        keyPoints.push(i);
      }
      // Significant change (more than 10 points)
      else if (Math.abs(current - prev) > 10 || Math.abs(current - next) > 10) {
        keyPoints.push(i);
      }
    }
    
    // Keep only unique indices
    return [...new Set(keyPoints)].sort((a, b) => a - b);
  };

  // Calculate trend line data
  const calculateTrendLine = (data: number[]): number[] => {
    if (data.length <= 1) return data;
    
    const n = data.length;
    let sum_x = 0;
    let sum_y = 0;
    let sum_xy = 0;
    let sum_xx = 0;
    let validPoints = 0;
    
    // Calculate sums for linear regression, skipping null values
    for (let i = 0; i < n; i++) {
      const y = data[i];
      if (y !== null && y !== undefined) {
        sum_x += i;
        sum_y += y;
        sum_xy += i * y;
        sum_xx += i * i;
        validPoints++;
      }
    }
    
    // Not enough valid points for regression
    if (validPoints < 2) return data;
    
    // Calculate slope and intercept
    const slope = (validPoints * sum_xy - sum_x * sum_y) / (validPoints * sum_xx - sum_x * sum_x);
    const intercept = (sum_y - slope * sum_x) / validPoints;
    
    // Generate trend line data
    return data.map((_, i) => {
      const y = slope * i + intercept;
      // Return 0 if out of range (instead of null)
      return y > 100 || y < 0 ? 0 : y;
    });
  };

  const keyPoints = findKeyDataPoints();
  
  const chartData: ChartData<'line', number[]> = {
    labels: visibleData.map(d => d.month),
    datasets: [
      {
        label: 'Spending',
        data: visibleData.map(d => d.factor_spending ?? 0),
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: (ctx) => {
          // Show points only for key points to reduce visual clutter
          return keyPoints.includes(ctx.dataIndex) ? 3 : 0;
        },
        pointHoverRadius: 5,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: CHART_COLOR,
        pointBorderWidth: 2,
        spanGaps: true,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
      },
      {
        label: 'Trend',
        data: calculateTrendLine(visibleData.map(d => d.factor_spending ?? 0)),
        fill: false,
        tension: 0.2, // Slight curve for trend line
        borderWidth: 2, // Slightly thicker line
        borderDash: [6, 4], // More visible dash pattern
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)', // Increased opacity
        spanGaps: false,
      }
    ]
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 2,
        left: 0,   // Remove left padding entirely
        right: 0,  // Remove right padding entirely
        top: 2
      }
    },
    animation: {
      duration: 750, // Smoother animations
      easing: 'easeOutQuart',
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 0, // Hide points by default (shown selectively with the pointRadius function)
        hitRadius: 10,
        hoverRadius: 5,
        hoverBorderWidth: 2
      }
    },
    transitions: {
      active: {
        animation: {
          duration: 150 // Slightly faster transitions
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
      axis: 'x',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
            weight: 500,
          },
          color: '#64748B',
          maxRotation: 45,
          minRotation: 45,
          callback: function(_value: any, index: any) {
            try {
              const date = parseISO(visibleData[index].month);
              return format(date, 'MMM yyyy');
            } catch (e) {
              return visibleData[index]?.month || '';
            }
          }
        },
      },
      y: {
        position: 'left',
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(226, 232, 240, 0.6)',
          drawTicks: false,
        },
        ticks: {
          font: {
            size: 11,
            family: "'Manrope', sans-serif",
          },
          color: '#64748B',
          padding: 10,
          stepSize: 20,
          callback: function(tickValue: string | number) {
            return tickValue.toString();
          },
        },
        border: {
          dash: [4, 4],
          display: false,
        },
      },
    },
    plugins: {
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1C4583',
        titleFont: {
          size: 14,
          weight: 600,
          family: "'Manrope', sans-serif",
        },
        bodyColor: '#64748B',
        bodyFont: {
          size: 13,
          family: "'Manrope', sans-serif",
        },
        padding: 12,
        borderColor: '#E2E8F0',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          title: (tooltipItems: any) => {
            try {
              if (!tooltipItems?.[0]?.label) return '';
              const dataIndex = tooltipItems[0].dataIndex;
              const date = parseISO(visibleData[dataIndex].month);
              return format(date, 'MMMM yyyy');
            } catch (e) {
              return tooltipItems?.[0]?.label || '';
            }
          },
          label: (context: any) => {
            if (context.dataset.label === 'Trend') {
              return `Average Score: ${context.raw !== null ? Math.round(context.raw) : '--'}`;
            }
            
            const score = context.raw;
            
            // Get previous month data for comparison if available
            let changeText = '';
            if (context.dataIndex > 0) {
              const prevScore = visibleData[context.dataIndex - 1].factor_spending ?? 0;
              const change = score - prevScore;
              changeText = `Change: ${change >= 0 ? '+' : ''}${Math.round(change)}`;
            }
            
            return [
              `Month Score: ${score !== null ? Math.round(score) : '--'}`,
              `Status: ${HEALTH_STATUS_TEXT[getHealthStatusCode(score)]}`,
              changeText
            ].filter(Boolean); // Filter out empty change text if not available
          },
        },
      },
      legend: {
        display: false,
      },
    },
  };

  // Add touch navigation handler functions after chartRef declaration
  // Handle touch/mouse start
  const handleDragStart = useCallback((clientX: number) => {
    setTouchStartX(clientX);
    setIsDragging(true);
  }, []);

  // Handle touch/mouse move
  const handleDragMove = useCallback((clientX: number) => {
    if (!isDragging || touchStartX === null) return;
    
    const deltaX = touchStartX - clientX;
    if (Math.abs(deltaX) < 5) return; // Ignore tiny movements
    
    const containerWidth = containerRef.current?.clientWidth || 300;
    const moveRatio = deltaX / containerWidth;
    const dataRangeSize = data.length;
    const viewWindowSize = viewWindow.end - viewWindow.start;
    
    // Calculate how much to shift the view window (0.5 = 50% of visible range)
    const shiftAmount = Math.ceil(moveRatio * viewWindowSize * 0.5);
    
    if (shiftAmount !== 0) {
      // Calculate new window bounds
      let newStart = viewWindow.start + shiftAmount;
      let newEnd = viewWindow.end + shiftAmount;
      
      // Ensure window stays within data bounds
      if (newStart < 0) {
        newStart = 0;
        newEnd = viewWindowSize;
      }
      
      if (newEnd > dataRangeSize) {
        newEnd = dataRangeSize;
        newStart = Math.max(0, dataRangeSize - viewWindowSize);
      }
      
      // Update view window and visible data
      if (newStart !== viewWindow.start || newEnd !== viewWindow.end) {
        setViewWindow({ start: newStart, end: newEnd });
        const newVisibleData = data.slice(newStart, newEnd);
        handleDataChange(newVisibleData);
        
        // Reset touch tracking after making the change
        setTouchStartX(clientX);
      }
    }
  }, [data, isDragging, touchStartX, viewWindow]);

  // Handle touch/mouse end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setTouchStartX(null);
  }, []);

  // Fix the handleDataChange function to handle circular dependencies
  const handleDataChange = useCallback((newVisibleData: FinancialHealthStats[]) => {
    // Temporarily disable animations when changing data
    const chart = chartRef.current;
    if (chart) {
      // Store original animation settings
      const originalAnimation = chart.options.animation;
      
      // Disable animations and update data in one go
      chart.options.animation = false;
      
      // Update the data directly in the chart to avoid re-renders
      chart.data.labels = newVisibleData.map(d => d.month);
      chart.data.datasets[0].data = newVisibleData.map(d => d.factor_spending ?? 0);
      if (chart.data.datasets[1]) {
        chart.data.datasets[1].data = calculateTrendLine(newVisibleData.map(d => d.factor_spending ?? 0));
      }
      
      // Update the chart with 'none' mode for instant update
      chart.update('none');
      
      // Update state after chart is updated
      setVisibleData(newVisibleData);
      
      // Re-enable animations after a short delay
      setTimeout(() => {
        if (chart) {
          chart.options.animation = originalAnimation;
        }
      }, 100);
    } else {
      setVisibleData(newVisibleData);
    }
  }, []);

  return (
    <div className="relative w-full flex flex-col" style={{ 
      height: '100%', 
      minHeight: '100%',
      marginBottom: '0',
      paddingBottom: '0',
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Add debug info for development environment */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div className="absolute top-0 right-0 bg-white/80 p-2 text-xs text-slate-500 z-10 rounded border">
          <div>Total months: {data.length}</div>
          <div>Visible: {visibleData.length}</div>
          <div>Window: {viewWindow.start} - {viewWindow.end}</div>
        </div>
      )}
      
      {/* Navigation header with months and navigation buttons */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm text-slate-700">
          <span className="font-semibold">{viewWindow.end - viewWindow.start}</span> of <span className="font-semibold">{data.length}</span> months shown
          {data.length < 24 && <span className="ml-1 text-amber-500 text-xs">(Limited data available)</span>}
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            className="text-xs px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            onClick={() => {
              const newStart = Math.max(0, viewWindow.start - Math.floor(monthsToShow / 2));
              const newEnd = Math.min(data.length, newStart + monthsToShow);
              
              // Update view window
              setViewWindow({ start: newStart, end: newEnd });
              
              // Also update visibleData through handleDataChange
              handleDataChange(data.slice(newStart, newEnd));
            }}
          >
            ← Earlier
          </button>
          
          <button 
            className="text-xs px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            onClick={() => {
              const newStart = Math.min(data.length - monthsToShow, viewWindow.start + Math.floor(monthsToShow / 2));
              const newEnd = Math.min(data.length, newStart + monthsToShow);
              
              // Update view window
              setViewWindow({ start: newStart, end: newEnd });
              
              // Also update visibleData through handleDataChange
              handleDataChange(data.slice(newStart, newEnd));
            }}
          >
            Later →
          </button>
        </div>
      </div>
      
      {/* Glassmorphic chart container */}
      <div 
        ref={containerRef}
        className="p-0 rounded-xl overflow-hidden w-full flex-grow"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.03), 0 1px 6px rgba(0, 0, 0, 0.02)',
          height: 'calc(100% - 40px)', // Subtract space for x-axis labels
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: 'grab',
          touchAction: 'pan-y',
          userSelect: 'none'
        }}
        onTouchStart={(e) => {
          if (e.touches.length === 1) {
            const touch = e.touches[0];
            handleDragStart(touch.clientX);
          }
        }}
        onTouchMove={(e) => {
          if (e.touches.length === 1 && isDragging) {
            const touch = e.touches[0];
            handleDragMove(touch.clientX);
          }
        }}
        onTouchEnd={() => {
          handleDragEnd();
        }}
        onMouseDown={(e) => {
          handleDragStart(e.clientX);
        }}
        onMouseMove={(e) => {
          if (isDragging) {
            handleDragMove(e.clientX);
          }
        }}
        onMouseUp={() => {
          handleDragEnd();
        }}
        onMouseLeave={() => {
          if (isDragging) {
            handleDragEnd();
          }
        }}
      >
        <div 
          className="relative h-full pb-6"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        > 
          <ChartOverlayWrapper overlay={null} showStatsButton={false}>
            <Chart
              ref={chartRef}
              type="line"
              data={chartData}
              options={options}
            />
          </ChartOverlayWrapper>
        </div>
      </div>
      
      <ChartExpandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={data}
        title="Spending"
        dataKey="factor_spending"
        color={CHART_COLOR}
        yAxisFormatter={(value: number) => value.toString()}
        min={0}
        max={100}
      />
    </div>
  );
};

export default SpendingFactorChart;
